import { useMemo, useState } from "react";
import { MagnifyingGlass, Plus, X } from "@phosphor-icons/react";

// Type to search the shop's color list, pick from the matches, or add your own name.
export const ColorPicker = ({
  options = [],
  value = [],
  onChange,
  testId = "color-picker",
}) => {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const term = q.trim().toLowerCase();

  const matches = useMemo(() => {
    if (!term) return [];
    return options
      .filter((c) => !value.includes(c) && c.toLowerCase().includes(term))
      .slice(0, 8);
  }, [options, value, term]);
  const exact =
    options.some((c) => c.toLowerCase() === term) ||
    value.some((c) => c.toLowerCase() === term);

  const add = (c) => {
    const name = c.trim();
    if (!name || value.some((v) => v.toLowerCase() === name.toLowerCase()))
      return;
    onChange([...value, name]);
    setQ("");
    setOpen(false);
  };
  const remove = (c) => onChange(value.filter((v) => v !== c));

  const onKey = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      add(matches[0] && !exact && term.length < 3 ? matches[0] : q);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div className="space-y-2" data-testid={testId}>
      {value.length > 0 && (
        <div
          className="flex flex-wrap gap-2"
          data-testid={`${testId}-selected`}
        >
          {value.map((c) => (
            <span
              key={c}
              data-testid={`${testId}-chip-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="inline-flex items-center gap-1.5 rounded-full bg-indigo-500/15 py-1.5 pl-3 pr-1.5 text-xs text-indigo-200 ring-1 ring-indigo-500/30"
            >
              {c}
              <button
                type="button"
                aria-label={`Remove ${c}`}
                data-testid={`${testId}-remove-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                onClick={() => remove(c)}
                className="flex h-5 w-5 items-center justify-center rounded-full text-indigo-300 transition-colors duration-150 hover:bg-indigo-500/30 hover:text-white"
              >
                <X size={11} weight="bold" />
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="relative">
        <MagnifyingGlass
          size={15}
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500"
        />
        <input
          data-testid={`${testId}-input`}
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 120)}
          onKeyDown={onKey}
          placeholder="Search a color, or type your own and press Enter"
          className="h-12 w-full rounded-lg border border-zinc-800 bg-zinc-950 pl-9 pr-24 text-sm text-zinc-100 outline-none transition-colors duration-150 focus:border-indigo-500"
        />
        {term && (
          <button
            type="button"
            data-testid={`${testId}-add-custom`}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => add(q)}
            className="absolute right-2 top-1/2 flex h-8 -translate-y-1/2 items-center gap-1 rounded-md bg-zinc-800 px-2.5 text-[11px] font-semibold uppercase tracking-wider text-zinc-200 transition-colors duration-150 hover:bg-indigo-600 hover:text-white"
          >
            <Plus size={12} weight="bold" /> Add
          </button>
        )}
        {open && term && matches.length > 0 && (
          <ul
            data-testid={`${testId}-matches`}
            className="absolute z-20 mt-1 max-h-60 w-full overflow-y-auto rounded-lg border border-zinc-800 bg-zinc-950 py-1 shadow-xl"
          >
            {matches.map((c) => (
              <li key={c}>
                <button
                  type="button"
                  data-testid={`${testId}-match-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => add(c)}
                  className="flex w-full items-center justify-between px-3 py-2 text-left text-sm text-zinc-300 transition-colors duration-150 hover:bg-zinc-900 hover:text-white"
                >
                  {c}
                  <Plus size={12} className="text-zinc-500" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <p className="text-[11px] text-zinc-500">
        {value.length
          ? `${value.length} color${value.length === 1 ? "" : "s"} picked · start typing to add another`
          : "Start typing — pick from our slabs or add any color name you have in mind."}
      </p>
    </div>
  );
};

export default ColorPicker;
